import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import LeftNav from '../common/LeftNav';
import TopNav from '../common/TopNav';
import propertyService from '../../../services/propertyService';
import '../../../assets/styles/PersonalIDs.css';
import '../../assets/styles/CRMOpenLead.css';
import arrowleftt from '../../assets/img/arrowleftt.svg';

const STATUS_COLORS = {
  active:   { bg: '#D1FAE5', text: '#059669' },
  pending:  { bg: '#FEF3C7', text: '#D97706' },
  approved: { bg: '#D1FAE5', text: '#059669' },
  rejected: { bg: '#FEE2E2', text: '#DC2626' },
  deactive: { bg: '#F3F4F6', text: '#6B7280' },
  inactive: { bg: '#F3F4F6', text: '#6B7280' },
};

const TABS = [
  { key: 'all',      label: 'All'      },
  { key: 'active',   label: 'Active'   },
  // { key: 'pending',  label: 'Pending'  },
  // { key: 'rejected', label: 'Rejected' },
  { key: 'deactive', label: 'Deactive' },
];

const API_BASE = process.env.REACT_APP_API_BASE_URL || 'https://sandbox.rebax.in';

// Prefers the listing thumbnail; falls back to the first gallery image.
const getFirstImage = (images, thumbnail) => {
  try {
    const arr = typeof images === 'string' ? JSON.parse(images) : images;
    const img = thumbnail || (Array.isArray(arr) && arr.length > 0 ? arr[0] : null);
    if (!img) return null;
    return img.startsWith('http') ? img : `${API_BASE}${img}`;
  } catch { return null; }
};

const formatCurrency = (min, max) => {
  const fmt = (v) => {
    if (!v) return null;
    if (v >= 10000000) return `${(v / 10000000).toFixed(1)} Cr`;
    if (v >= 100000)   return `${(v / 100000).toFixed(1)} L`;
    return `${(v / 1000).toFixed(0)}K`;
  };
  const a = fmt(min), b = fmt(max);
  if (a && b) return `${a} - ${b}`;
  return a || b || 'N/A';
};

// Read-only view of the listings an agent/agency added from their own Add Listing screen.
export default function AdminCrmListings() {
  const navigate = useNavigate();
  const location = useLocation();
  const { userId, userName, userRole } = location.state || {};

  const [loading,     setLoading]     = useState(true);
  const [allListings, setAllListings] = useState([]);
  const [activeTab,   setActiveTab]   = useState('all');
  const [search,      setSearch]      = useState('');

  const fetchListings = useCallback(async () => {
    if (!userId) { setLoading(false); return; }
    setLoading(true);
    try {
      const res = await propertyService.getListingsByUser(userId, userRole);
      setAllListings(res.success ? res.data : []);
    } catch (err) {
      console.error('Fetch listings error:', err);
    } finally {
      setLoading(false);
    }
  }, [userId, userRole]);

  useEffect(() => { fetchListings(); }, [fetchListings]);

  const matchesTab = (listing, tab) => {
    const approval = listing.approval_status || 'approved';
    if (tab === 'all') return true;
    if (tab === 'active') return listing.status === 'active' && approval === 'approved';
    if (tab === 'deactive') return listing.status !== 'active' && approval !== 'rejected' && approval !== 'pending';
    if (tab === 'pending') return approval === 'pending';
    if (tab === 'rejected') return approval === 'rejected';
    return true;
  };

  const matchesSearch = (p) => !search.trim() || (p.property_title || '').toLowerCase().includes(search.trim().toLowerCase());

  const listings = allListings.filter(p => matchesSearch(p) && matchesTab(p, activeTab));
  const getTabCount = (tab) => allListings.filter(p => matchesSearch(p) && matchesTab(p, tab)).length;

  const openListingDetail = (listing) => {
    navigate(`/admin-projectdetails/${listing.id}`, { state: { project: listing } });
  };

  return (
    <div className="d-flex" style={{ background: '#F9FAFB', minHeight: '100vh' }}>
      <LeftNav />
      <div className="main-content flex-grow-1">
        <TopNav />
        <div className="p-3" style={{ paddingBottom: '100px' }}>

          {/* Back + Title */}
          <div className="d-flex align-items-center justify-content-between mb-3">
            <div className="d-flex align-items-center gap-2">
              <img src={arrowleftt} onClick={() => navigate(-1)} style={{ cursor: 'pointer' }} alt="back" />
              <h6 className="accordion-title mb-0">Add Listing</h6>
            </div>
            <span style={{ fontSize: '13px', fontWeight: '600', color: '#2563EB', backgroundColor: '#EFF6FF', padding: '4px 12px', borderRadius: '10px' }}>
              {listings.length}
            </span>
          </div>

          {/* Search */}
          <div className="d-flex gap-2 mb-3">
            <input
              type="text"
              className="form-control personal-input"
              placeholder="Search listings..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && e.preventDefault()}
            />
          </div>

          {/* Tabs */}
          <div className="d-flex gap-2 mb-3" style={{ flexWrap: 'wrap' }}>
            {TABS.map(tab => (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                style={{
                  padding: '6px 14px', borderRadius: '16px', border: 'none',
                  backgroundColor: activeTab === tab.key ? '#2563EB' : '#F3F4F6',
                  color: activeTab === tab.key ? '#fff' : '#6B7280',
                  fontSize: '12px', fontWeight: '500', cursor: 'pointer'
                }}
              >{tab.label} ({getTabCount(tab.key)})</button>
            ))}
          </div>

          {userName && <p style={{ fontSize: '13px', fontWeight: '600', color: '#6B7280', marginBottom: '12px' }}>{userName}</p>}

          {/* Listing grid */}
          {loading ? (
            <div className="text-center py-5"><div className="spinner-border text-primary" role="status"><span className="visually-hidden">Loading...</span></div><div className="mt-3" style={{ color: '#888' }}>Loading...</div></div>
          ) : listings.length === 0 ? (
            <div className="text-center py-5">
              <div style={{ fontSize: '48px' }}>🏠</div>
              <div style={{ fontSize: '17px', fontWeight: '600', color: '#374151', marginTop: '12px' }}>No Listings</div>
              <div style={{ fontSize: '14px', color: '#9CA3AF', marginTop: '6px' }}>No listings found for this {userRole || 'user'}</div>
            </div>
          ) : (
            <div className="row g-3">
              {listings.map((item, index) => {
                const statusC   = STATUS_COLORS[item.status]          || STATUS_COLORS.inactive;
                const approvalC = STATUS_COLORS[item.approval_status] || STATUS_COLORS.pending;
                const imageUrl  = getFirstImage(item.images, item.thumbnail);
                return (
                  <div key={item.id || index} className="col-12 col-md-6 col-xl-4">
                    <div
                      onClick={() => openListingDetail(item)}
                      style={{
                        backgroundColor: '#fff', borderRadius: '14px', overflow: 'hidden',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.06)', border: '1px solid #F3F4F6',
                        height: '100%', display: 'flex', flexDirection: 'column', cursor: 'pointer'
                      }}>

                      {/* Image + approval badge */}
                      <div style={{ position: 'relative', height: '160px', backgroundColor: '#F3F4F6', flexShrink: 0 }}>
                        {imageUrl
                          ? <img src={imageUrl} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} onError={e => { e.target.style.display = 'none'; }} />
                          : <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '40px' }}>🏗️</div>
                        }
                        <span style={{
                          position: 'absolute', bottom: 8, left: 8,
                          backgroundColor: approvalC.bg, color: approvalC.text,
                          padding: '3px 8px', borderRadius: '6px', fontSize: '10px', fontWeight: '700',
                          textTransform: 'capitalize'
                        }}>{item.approval_status || 'pending'}</span>
                      </div>

                      {/* Card body */}
                      <div style={{ padding: '12px', flex: 1, display: 'flex', flexDirection: 'column' }}>
                        <div style={{ fontSize: '15px', fontWeight: '700', color: '#111827', marginBottom: '4px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {item.property_title || 'Untitled'}
                        </div>
                        {item.project_code && (
                          <div style={{ fontSize: '12px', color: '#6B7280', marginBottom: '2px' }}>ID: {item.project_code}</div>
                        )}
                        {item.city && (
                          <div style={{ fontSize: '12px', color: '#9CA3AF', marginBottom: '2px' }}>
                            📍 {item.city}{item.locality ? `, ${item.locality}` : ''}
                          </div>
                        )}
                        {item.property_type && (
                          <div style={{ fontSize: '12px', color: '#9CA3AF', marginBottom: '8px' }}>🏠 {item.property_type}</div>
                        )}

                        {/* BHK + sqft tags */}
                        <div className="d-flex gap-1 flex-wrap mb-2">
                          {item.bedrooms && <span style={{ fontSize: '10px', fontWeight: '500', color: '#6B7280', backgroundColor: '#F3F4F6', padding: '2px 7px', borderRadius: '4px' }}>{item.bedrooms} BHK</span>}
                          {item.area_sqft && <span style={{ fontSize: '10px', fontWeight: '500', color: '#6B7280', backgroundColor: '#F3F4F6', padding: '2px 7px', borderRadius: '4px' }}>{item.area_sqft} sqft</span>}
                        </div>

                        {/* Footer: price + status + views */}
                        <div style={{ marginTop: 'auto' }}>
                          <div className="d-flex align-items-center justify-content-between mb-1">
                            <span style={{ fontSize: '14px', fontWeight: '700', color: '#2563EB' }}>
                              {formatCurrency(item.price_min || item.price, item.price_max)}
                            </span>
                            <span style={{ backgroundColor: statusC.bg, color: statusC.text, padding: '3px 8px', borderRadius: '6px', fontSize: '10px', fontWeight: '600', textTransform: 'capitalize' }}>{item.status}</span>
                          </div>
                          <div className="d-flex align-items-center justify-content-between">
                            <span style={{ fontSize: '11px', color: '#9CA3AF' }}>👁 {item.views_count || 0} views</span>
                            <span style={{ fontSize: '11px', color: '#D1D5DB' }}>
                              {item.created_at ? new Date(item.created_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: '2-digit' }) : ''}
                            </span>
                          </div>
                        </div>

                        {/* Rejection reason */}
                        {item.approval_status === 'rejected' && item.rejection_reason && (
                          <div className="d-flex gap-2 mt-2 p-2" style={{ backgroundColor: '#FEF2F2', borderRadius: '8px' }}>
                            <span>⚠️</span>
                            <span style={{ fontSize: '11px', color: '#DC2626' }}>{item.rejection_reason}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
